import dayjs from 'dayjs';

import { CommandTypeEnum, DeviceTypeEnum, HardwareProviderEnum, LockStateEnum } from '~enums';
import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import { getConnectivityProfile } from '~interfaces/connectivityProfile';
import Device from '~interfaces/device';
import DeviceModel from '~interfaces/deviceModel';
import { SkcDataResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

import DeviceType from '../../../interfaces/DeviceType';
import ChargingStateEnum from '../enums/chargingStateEnum';
import DeviceWriteable from '../interfaces/deviceWriteable';

class DevicesService extends SkopeiConnectService {
	protected readonly path = 'devices';

	/**
	 * Gets the list of devices from the internal endpoint.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the device name, hardware id or bluetooth name.
	 * @param deviceModelName Filter on device model name.
	 * @param resourceGroupId Filter on resource group id.
	 * @param organisationId Filter on organisation id.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns List of devices.
	 */
	async getDevices(
		params: {
			page: string;
			pageSize: string;
			searchQuery?: string;
			deviceModelName?: string;
			resourceGroupId?: string;
			organisationId?: string;
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<Device>> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.get<SkcOffsetPagedResponse<DeviceResponse>>(endpoint, {
				signal: signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					search: params.searchQuery?.trim() || undefined,
					model: params.deviceModelName,
					'resource-group-id': params.resourceGroupId,
					'client-id': params.organisationId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<Device>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, DevicesService.fromResponse)
				);
			});
	}

	public getDevicesCacheKey(
		searchParams: URLSearchParams,
		overwrittenClientId?: string | undefined,
		listType?: string | undefined
	): (string | URLSearchParams | undefined)[] {
		return [this.path, searchParams.toString(), overwrittenClientId, listType];
	}

	/**
	 * Gets a device by id.
	 *
	 * @param deviceId The id of the device.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The device.
	 */
	async getDeviceById(
		deviceId: string,
		overwrittenClientId?: string | undefined | null
	): Promise<Device | undefined> {
		const endpoint = this.createEndpoint('v1', [deviceId]);
		return await this.client
			.get<SkcDataResponse<DeviceResponse>>(endpoint, {
				headers: {
					OverwrittenClientId: overwrittenClientId,
				},
			})
			.then((response) => {
				if (!response || response.data == undefined) {
					return Promise.reject(ApiError.createFromCustomCode('DEV_002'));
				}

				return Promise.resolve(DevicesService.fromResponse(response.data.data));
			})
			.catch((error: ApiError) => {
				if (error.errorCode == 'SKC_004') {
					error.setDescription(i18n.t('server.deviceNotFoundException', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	public getDeviceByIdCacheKey(
		deviceId?: string,
		overwrittenClientId?: string | undefined
	): (string | undefined)[] {
		return [this.path, deviceId, overwrittenClientId];
	}

	/**
	 * Updates a device,
	 *
	 * @param deviceId The id of the device that's to be updated.
	 * @param device The device with the new values.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The updated device.
	 */
	async updateDevice(
		deviceId: string,
		device: Partial<Device>,
		organisationId?: string | undefined
	): Promise<Device> {
		const endpoint = this.createEndpoint('v2', [deviceId]);
		const body =
			device.state?.connectivityProfile ?
				{
					...device,
					state: {
						...device.state,
						connectivityProfile: device.state.connectivityProfile.id,
					},
				}
			:	device;

		return await this.client
			.patch<DeviceResponse>(endpoint, body, {
				headers: {
					OverwrittenClientId: organisationId,
					'X-Set-Fields': ['description'],
				},
			})
			.then((response) => {
				if (!response || response.data == undefined) {
					return Promise.reject(ApiError.createFromCustomCode('DEV_002'));
				}

				return Promise.resolve({
					...DevicesService.fromResponse(response.data),
					state: device.state,
				} as Device);
			})
			.catch((error: ApiError) => {
				if (error.errorCode == 'SKC_004') {
					error.setDescription(i18n.t('server.deviceNotFoundException', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	/**
	 * Deletes a device.
	 *
	 * @param deviceId The id of the device.
	 *
	 * @returns The backend response.
	 */
	async deleteDevice(deviceId: string) {
		const endpoint = this.createEndpoint('v1', [deviceId]);
		const response = await this.client.delete(endpoint);
		return response.data;
	}

	/**
	 * Executes a command on the given device.
	 *
	 * @param deviceId The id of the device to execute the command on.
	 * @param command The command to execute.
	 * @param deviceORganisationId Required for super admins to execute a command on a device which is in a different client.
	 * @param metadata Metadata value. For example the duration in minutes that office mode is enabled when sending enableOfficeMode command.
	 */
	async executeDeviceCommand(params: {
		deviceId: string;
		command: string;
		deviceOrganisationId?: string | undefined;
		metadata?: number;
	}): Promise<void> {
		const endpoint = this.createEndpoint('v2', [
			params.deviceId,
			'commands',
			params.command,
			'execute',
		]);
		await this.client
			.post(
				endpoint,
				{ meta: params.metadata },
				{
					headers: {
						OverwrittenClientId: params.deviceOrganisationId,
					},
				}
			)
			.catch((error: ApiError) => {
				switch (error.errorCode) {
					case 'SKC_010':
						error.setDescription(
							i18n.t('executedInvalidCommand', { ns: 'errors', details: error.detail ?? '' })
						);
						break;
				}
				return Promise.reject(error);
			});
	}

	/**
	 * Gets all know devices types on the backend.
	 * @returns Array of DeviceType.
	 */
	async getDeviceTypes(): Promise<DeviceType[]> {
		const endpoint = this.createEndpoint('v1', ['types']);
		const response = await this.client.get(endpoint);
		return response.data.data;
	}

	/**
	 * Gets all know device models on the backend.
	 * @returns Array of DeviceModel.
	 */
	async getDeviceModels(): Promise<DeviceModel[]> {
		const endpoint = this.createEndpoint('v1', ['models']);
		const response = await this.client.get(endpoint);
		return response.data.data;
	}

	/**
	 * Model parsing.
	 */
	static fromResponse(response: DeviceResponse): Device {
		const { id, state, model, clientId, resourceGroup, resourceGroupId, clientName, ...rest } =
			response;

		// Timestamp is utc on the backend, but doesn't include the Z to indicate. When it does
		// get added, we simply use the string as is.
		const lastReportDateString =
			state?.lastReport?.endsWith('Z') ? state.lastReport : state?.lastReport + 'Z';

		return {
			...rest,
			organisationId: clientId?.toString(),
			organisationName: clientName,
			resourceGroupId: resourceGroupId?.toString() ?? resourceGroup?.toString(),
			state:
				state ?
					{
						...state,
						location:
							state?.latitude && state?.longitude ?
								{ latitude: state.latitude, longitude: state.longitude }
							:	undefined,
						lockState: state?.lockState as LockStateEnum,
						chargingState: state?.chargingState as ChargingStateEnum,
						lastReport: state?.lastReport ? dayjs(lastReportDateString) : undefined,
						temperature: state.temperature != undefined ? state.temperature : undefined,
						connectivityProfile: getConnectivityProfile(state.connectivityProfile),
					}
				:	undefined,
			model: {
				...model,
				id: model.id.toString(),
				name: model.name,
				deviceType: model.deviceType as DeviceTypeEnum,
				commandTypes: model.commandTypes as CommandTypeEnum[],
				hardwareProvider: model.hardwareProvider as HardwareProviderEnum,
			},
			id: id.toString(),
		};
	}

	static toRequest(data: DeviceWriteable): DeviceRequest {
		const { ...rest } = data;

		return {
			...rest,
		};
	}
}

interface DeviceResponse {
	id: number;
	resourceGroup?: number;
	resourceGroupId?: number;
	name: string;
	hardwareId: string;
	bluetoothName: string;
	description: string;
	clientId?: number;
	clientName?: string;
	state?: {
		id: string;
		batteryState: number;
		ianaTimeZoneId: string;
		firmware: string;
		lastEvent: number;
		lockState: string;
		chargingState: string;
		latitude?: number;
		longitude?: number;
		lastReport?: string;
		temperature?: number;
		connectivityProfile?: number;
	};
	model: {
		id: number;
		name: string;
		resourceGroupId?: number;
		description?: string;
		deviceType?: string;
		hardwareProvider?: string;
		commandTypes?: string[];
	};
}

type DeviceRequest = DeviceWriteable;

export default DevicesService;
